import React, { useState } from "react";
import { Plug, PlugZap, Clock, AlertTriangle, RefreshCw, DownloadCloud, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { sourceMeta, reviewsApi, btnCls } from "./shared";
import TaSourceActions from "./TaSourceActions";
import GoogleSourceActions from "./GoogleSourceActions";

const HOWTO = {
  gyg: "Supplier Portal yorum hesabı bağlı. Yeni yorumlar saatte bir otomatik çekilir; booking ref ile Canlı Sayfa'ya bağlanır.",
  viator: "Viator ürün yorumları TripAdvisor ürün sayfalarından (Apify) çekilir — gün hassasiyetinde tarih + reviewer adı. Booking ref olmadığı için yorumcu adı + tarih ile rezervasyon ÖNERİSİ üretilir; siz onaylarsınız.",
  tripadvisor: "TripAdvisor işletme sayfası (Apify) taranır; tam tarih + ürün adı + reviewer adı gelir. Yorumcu adı + tarih ile rezervasyon önerisi üretilir.",
  google: "Google Maps yorumları Apify üzerinden konum linklerinizden çekilir. Booking ref olmadığı için yorumcu adı + tarih ile rezervasyon ÖNERİSİ üretilir; siz onaylarsınız.",
};

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

function GygActions({ s, onChanged }) {
  const [busy, setBusy] = useState(null);
  const run = async (fn, key, okMsg) => {
    setBusy(key);
    try { const d = await fn(); toast.success(typeof okMsg === "function" ? okMsg(d) : okMsg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    setBusy(null);
  };
  const p = s.progress || {};
  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => run(reviewsApi.gygConnect, "connect", d => `Bağlandı: ${d.account}`)} disabled={!!busy || s.running}
          className={`${btnCls} ${s.connected ? "bg-slate-700 hover:bg-slate-600 text-slate-200" : "bg-blue-600 hover:bg-blue-500 text-white"}`} data-testid="gyg-connect-btn">
          {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} {s.connected ? "Yeniden Bağlan" : "Bağlan"}
        </button>
        <button onClick={() => run(() => reviewsApi.gygSync("incremental"), "inc", d => d.started ? "Yeni yorumlar çekiliyor…" : "Zaten çalışıyor")} disabled={!s.connected || !!busy || s.running}
          className={`${btnCls} bg-emerald-600 hover:bg-emerald-500 text-white`} data-testid="gyg-sync-btn">
          <RefreshCw className="w-4 h-4" /> Yeni Yorumları Çek
        </button>
        <button onClick={() => { if (window.confirm("Tüm geçmiş yorumlar (~98.000) çekilecek, birkaç dakika sürebilir. Devam?")) run(() => reviewsApi.gygSync("full"), "full", "Tüm geçmiş çekiliyor…"); }}
          disabled={!s.connected || !!busy || s.running} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200`} data-testid="gyg-full-sync-btn">
          <DownloadCloud className="w-4 h-4" /> Tüm Geçmişi Çek
        </button>
      </div>
      {(s.running || s.last_status === "running") && (
        <div className="flex items-center gap-2 text-xs text-emerald-300 bg-emerald-900/20 border border-emerald-800/40 rounded-lg px-3 py-2" data-testid="gyg-progress">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {p.mode === "full" ? "Tüm geçmiş" : "Yeni yorumlar"} çekiliyor: {p.fetched ?? 0}{p.total_remote ? ` / ${p.total_remote}` : ""} yorum · {p.inserted ?? 0} yeni · {p.matched ?? 0} eşleşti
        </div>
      )}
      {!s.running && s.last_status === "ok" && p.fetched != null && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1" data-testid="gyg-last-result"><History className="w-3 h-3" /> Son çekim: {p.fetched} yorum tarandı · {p.inserted} yeni · {p.matched} eşleşti{p.seconds ? ` · ${p.seconds}s` : ""}</div>
      )}
    </div>
  );
}

export default function SourcesTab({ sources, onChanged }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sources-tab">
      {(sources || []).map(s => {
        const m = sourceMeta(s.source);
        const connected = !!s.connected || ((s.source === "tripadvisor" || s.source === "viator" || s.source === "google") && (s.urls || []).length > 0);
        return (
          <div key={s.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4" data-testid={`source-card-${s.source}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: m.color, color: m.text || "#fff" }}>{m.short.slice(0, 3).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{s.name}</div>
                <div className="text-[11px] text-slate-400">{s.review_count} yorum sistemde{s.account ? ` · ${s.account}` : ""}</div>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full ${connected ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-400"}`} data-testid={`source-status-${s.source}`}>
                {connected ? <PlugZap className="w-3 h-3" /> : <Plug className="w-3 h-3" />} {connected ? "Bağlı" : "Bağlı değil"}
              </span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">{s.source === "gyg" && !connected ? "Supplier Portal yorum hesabıyla bağlanın; giriş + authenticator otomatik yapılır, sonra yorumlar saatte bir çekilir." : HOWTO[s.source]}</p>
            {s.source === "gyg" && <GygActions s={s} onChanged={onChanged} />}
            {(s.source === "tripadvisor" || s.source === "viator") && <TaSourceActions s={s} onChanged={onChanged} />}
            {s.source === "google" && <GoogleSourceActions s={s} onChanged={onChanged} />}
            <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Son çalışma: {fmtDT(s.last_run_at)}</span>
              {s.last_status === "error" && s.last_error && <span className="inline-flex items-center gap-1 text-rose-300" data-testid={`source-error-${s.source}`}><AlertTriangle className="w-3 h-3" /> {s.last_error}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
